import type { IconName } from '@sorrem-client/ui/components';
import {
    resolveDefeatItemLoss,
    resolveVeilSaturationById,
    type VeilSaturationBandDefinition,
    type VeilSaturationId,
} from '@sorrem/shared';

/** The color a trait's icon takes. */
export type CrucibleTraitTone = 'danger' | 'warning' | 'veil' | 'safe' | 'neutral';

/** One thing worth knowing about a crucible before entering, shown as an icon in its row and listed in the sidebar. */
export interface CrucibleTrait {
    readonly id: 'under-ready' | 'gear-at-risk' | 'takes-from-everyone' | 'gear-safe' | 'attackers';
    readonly icon: IconName;
    readonly tone: CrucibleTraitTone;
    /** A few words, e.g. "Gear at risk". */
    readonly label: string;
    /** What it means for this character, in a sentence or two; the row's tooltip. */
    readonly description: string;
    /** A number shown in the icon's corner, e.g. how many enemies attack at once. */
    readonly count?: number;
}

/**
 * What a crucible means for a character (readiness null while unknown):
 * whether they are under-ready, what a party wipe would take from them,
 * and how the band fights. Warnings come first.
 */
export function resolveCrucibleTraits(
    crucible: { readonly veilSaturation: VeilSaturationId; readonly recommendedReadiness: VeilSaturationBandDefinition['recommendedReadiness'] },
    characterReadiness: number | null,
): CrucibleTrait[] {
    const traits: CrucibleTrait[] = [];
    if (characterReadiness !== null && characterReadiness < crucible.recommendedReadiness.min) {
        traits.push({
            id: 'under-ready',
            icon: 'skull',
            tone: 'danger',
            label: 'Under-ready',
            description: 'This Crucible calls for more than you carry.',
        });
    }

    // While readiness is unknown, only a band that takes from everyone puts gear at risk.
    const itemLoss = resolveDefeatItemLoss(crucible.veilSaturation, characterReadiness ?? 0);
    if (itemLoss === 'every-member') {
        traits.push({
            id: 'takes-from-everyone',
            icon: 'error',
            tone: 'veil',
            label: 'Takes from everyone',
            description: 'Resonance runs too deep here for anyone to fall untouched: if your party falls, the Veil takes a carried item from everyone.',
        });
    } else if (itemLoss === 'above-band') {
        traits.push({
            id: 'gear-at-risk',
            icon: 'error',
            tone: 'warning',
            label: 'Gear at risk',
            description: 'You\'re stronger than this Crucible calls for. If your party falls, the Veil will take one of your carried items to restore the balance.',
        });
    } else if (characterReadiness !== null) {
        traits.push({
            id: 'gear-safe',
            icon: 'shield-check',
            tone: 'safe',
            label: 'Gear safe',
            description: 'If your party falls, the Veil takes nothing from you.',
        });
    }

    const attackers = resolveVeilSaturationById(crucible.veilSaturation).concurrentAttackersPerPlayer;
    traits.push({
        id: 'attackers',
        icon: 'swords',
        tone: 'neutral',
        count: attackers,
        label: attackers === 1 ? 'One attacker at a time' : `${attackers} attackers at once`,
        description: attackers === 1
            ? 'Enemies here attack you one at a time; the rest wait their turn.'
            : `Up to ${attackers} enemies here attack you at once; the rest wait their turn.`,
    });
    return traits;
}
